// Bottom left: the checklist (src/onboarding/Checklist.tsx), and what changed since your last visit: after three days or
// more away, the While you were away card (src/help/Away.tsx), and after an update What's new (src/help/news.ts).
// Both sit as small pills and open on a click, so they never cover the board.
//
// The bottom row's homes (Features 4, Tidy, David: "shouldn't all compete for the same corners. Give each a fixed home"):
// getting started on the left, the command bar in the middle, zoom on the right with toasts rising above it. The
// command bar measures the row and moves, folds or lifts what is here to keep each in its place (src/chrome/bottomRow.ts).

import { useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, ChevronDown, History, X } from "lucide-react";
import { formatNumber, timeAgo } from "../../shared/format";
import { cx } from "../lib/util";
import { get, set, useStore } from "../state/store";
import { camera } from "../canvas/camera";
import { switchBoard } from "../state/actions";
import { Who } from "./agentMarks";
import { adoptOldFlags } from "../state/onboarding";
import { Checklist } from "../onboarding/Checklist";
import { HelpNote } from "../help/HelpLayer";

// the checklist's ticks and its "hide for good" are the account's (shared/onboarding.ts), on every device
export { markThemed } from "../state/onboarding";

function SinceLast() {
  const since = useStore((s) => s.lastVisitAt);
  const activity = useStore((s) => s.activity);
  const streams = useStore((s) => s.streams);
  const items = useStore((s) => s.items);
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);
  const rows = useMemo(() => {
    if (!since) return [];
    const out: { key: string; t: number; who?: string; title: string; meta: string; delta?: { up: boolean; text: string }; itemId?: string }[] = [];
    for (const s of Object.values(streams)) {
      if (typeof s.value !== "number" || s.updatedAt <= since || s.points.length < 2) continue;
      const before = [...s.points].reverse().find((p) => p.t <= since);
      if (!before || before.v === s.value) continue;
      const pct = before.v !== 0 ? ((s.value - before.v) / Math.abs(before.v)) * 100 : null;
      const widget = Object.values(items).find((i) => i.kind === "widget" && i.stream === s.key);
      out.push({
        key: "s:" + s.key,
        t: s.updatedAt,
        title: widget && widget.kind === "widget" ? widget.title : (s.label ?? s.key),
        meta: `${formatNumber(before.v, s.format)} → ${formatNumber(s.value, s.format)}`,
        delta: { up: s.value > before.v, text: pct !== null ? `${Math.abs(pct) >= 10 ? Math.abs(pct).toFixed(0) : Math.abs(pct).toFixed(1)}%` : formatNumber(Math.abs(s.value - before.v), s.format) },
        itemId: widget?.id,
      });
    }
    for (const a of activity) {
      if (a.t <= since || a.who === "You" || a.kind === "system") continue;
      out.push({ key: "a:" + a.id, t: a.t, who: a.who, title: a.title, meta: `${a.who} · ${timeAgo(a.t)}`, itemId: a.itemId ?? undefined });
      if (out.length > 14) break;
    }
    return out.sort((a, b) => b.t - a.t).slice(0, 10);
  }, [since, activity, streams, items]);
  if (gone || !since || Date.now() - since < 45 * 60_000 || !rows.length) return null;
  const go = (id?: string) => {
    if (!id) return;
    const it = get().items[id];
    if (!it) return;
    if (it.boardId !== get().boardId) switchBoard(it.boardId);
    set({ selection: [id] });
    camera.fit({ x: it.x, y: it.y, w: it.w, h: it.h }, { maxZ: 1, pad: 140 });
  };
  const label = `Since ${timeAgo(since).replace("just now", "earlier")}`;
  if (!open)
    return (
      <button className="corner-pill island" onClick={() => setOpen(true)}>
        <History size={14} className="corner-pill-ico" />
        <span>{label}</span>
        <span className="chip is-accent">
          {rows.length} change{rows.length === 1 ? "" : "s"}
        </span>
      </button>
    );
  return (
    <div className="corner-card island">
      <div className="corner-head">
        <h3>{label}</h3>
        <span className="inline-actions">
          <button className="icon-btn sm" aria-label="Fold away" onClick={() => setOpen(false)}>
            <ChevronDown size={14} />
          </button>
          <button className="icon-btn sm" aria-label="Dismiss" onClick={() => setGone(true)}>
            <X size={13} />
          </button>
        </span>
      </div>
      <div className="since-list">
        {rows.map((r) => (
          <button key={r.key} className="since-row" onClick={() => go(r.itemId)}>
            {r.who ? (
              <Who name={r.who} />
            ) : (
              <span className={cx("delta", r.delta?.up ? "is-good" : "is-bad")}>
                {r.delta?.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                {r.delta?.text}
              </span>
            )}
            <span className="grow">
              <b>{r.title}</b>
              <span className="meta">{r.meta}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function Corner() {
  const mobile = useStore((s) => s.mobile);
  // this browser's flags from before the account kept them, read in once the board is here
  const ready = useStore((s) => !!s.workspace && !s.guest);
  useEffect(() => {
    if (!ready) return;
    // a moment after the board settles, so a second load of it (a sign-in finishing, a dev server's double start)
    // never lands over the change
    const t = setTimeout(adoptOldFlags, 1500);
    return () => clearTimeout(t);
  }, [ready]);
  if (mobile) return null;
  return (
    <div className="corner-stack">
      {/* one card at a time (Features 4): While you were away, else What's new, else the Since pill */}
      <HelpNote fallback={<SinceLast />} />
      <Checklist />
    </div>
  );
}
